/* AOP Budget — Details tab: one row per contract/opportunity/budget-only
   planning line for next fiscal year, grouped into sections by business
   type. See APIIntegrationFiles/AOP_BUDGET_API.md. Owns all filter/sort
   state and the data fetch; ./DetailsTable.js is pure rendering.
   Sorting/filtering (click-to-cycle column headers, filter-icon popovers)
   is the same system Waterfall EAC uses — sorting happens WITHIN each
   section so the business-type grouping never gets scrambled by a column
   sort. Section subtotals/grand total come straight from the API when no
   client-side filter narrows the rows; once one does, they're recomputed
   from the still-visible rows (same "recompute only when filtered" rule
   Monthly DFR's own Summary.js uses) so a filtered view never shows a
   subtotal for rows that aren't on screen. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getAOPBudgetDetailsAPI } from "../../../../api/reports-api";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
} from "../waterfall-eac/waterfallTableUtils";
import { calcFilterPos, calcOpDropdownPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import { TABLE_FONT } from "../../reportTableConfig";
import { mapAOPBudgetDetails } from "./aopBudgetUtils";
import DetailsTable from "./DetailsTable";

const FONT = TABLE_FONT;

function applyNumFilter(v, f) {
  if (!f?.value) return true;
  const n = Number(v);
  const fv = Number(f.value);
  if (Number.isNaN(n) || Number.isNaN(fv)) return false;
  switch (f.op) {
    case "gt": return n > fv;
    case "gte": return n >= fv;
    case "lt": return n < fv;
    case "lte": return n <= fv;
    case "eq": return n === fv;
    case "neq": return n !== fv;
    default: return true;
  }
}

const SORT_LABEL = {
  name: "Name",
  id: "Contract ID",
  revenue: "Revenue",
  revenueStartMonth: "Revenue Start Month",
  revenueThruMonth: "Revenue Thru Month",
  popStartDate: "POP Start Date",
  popEndDate: "POP End Date",
};

export default function Details({ filters, version, onMeta }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [filterText, setFilterText] = useState({ name: "", id: "" });
  const [colFilters, setColFilters] = useState({});
  const [sortKeys, setSortKeys] = useState([]);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opPos, setOpPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  const apiFilterKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAOPBudgetDetailsAPI(filters, version)
      .then((res) => {
        if (cancelled) return;
        if (res?.data) {
          const mapped = mapAOPBudgetDetails(res);
          setData(mapped);
          onMeta?.({ nextYear: mapped.nextYear });
        } else {
          setError(res?.message || "Failed to load data");
          setData(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load data");
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilterKey, version]);

  /* Close filter panel / op dropdown on outside click */
  useEffect(() => {
    if (!openFilterKey) return undefined;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      ) {
        setOpenFilterKey(null);
        setOpenOpKey(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!openOpKey) return undefined;
    const h = (e) => {
      if (!e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-btn]")) setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => { openFilterKeyRef.current = openFilterKey; }, [openFilterKey]);
  useEffect(() => { openOpKeyRef.current = openOpKey; }, [openOpKey]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) {
      const btn = filterBtnRefs.current[key];
      if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    }
    const opKey = openOpKeyRef.current;
    if (opKey) {
      const opBtn = opBtnRefs.current[opKey];
      if (opBtn) setOpPos(calcOpDropdownPos(opBtn));
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => {
      window.removeEventListener("scroll", repositionPanels, true);
      window.removeEventListener("resize", repositionPanels);
    };
  }, [repositionPanels]);

  function hasFilterValue(key) {
    if (key === "name" || key === "id") return !!filterText[key];
    return !!colFilters[key]?.value;
  }

  function toggleFilter(key, btnEl) {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    const btn = btnEl || filterBtnRefs.current[key];
    if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  }

  function toggleOpDropdown(key) {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (!btn) return;
    setOpPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  }

  const handleSort = useCallback((colKey) => setSortKeys((prev) => toggleSortKey(prev, colKey)), []);
  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);
  const getSortValue = useCallback((row, key) => (key === "revenue" ? Number(row.revenue) || 0 : row[key] ?? ""), []);
  const getSortLabel = useCallback((key) => SORT_LABEL[key] || key, []);

  const hasTableFilters = Boolean(filterText.name.trim() || filterText.id.trim() || colFilters.revenue?.value);

  const rowMatches = useCallback(
    (r) => {
      const nameQ = filterText.name.trim().toLowerCase();
      const idQ = filterText.id.trim().toLowerCase();
      if (nameQ && !String(r.name || "").toLowerCase().includes(nameQ)) return false;
      if (idQ && !String(r.id || "").toLowerCase().includes(idQ)) return false;
      if (colFilters.revenue?.value && !applyNumFilter(r.revenue, colFilters.revenue)) return false;
      return true;
    },
    [filterText, colFilters],
  );

  const visibleSections = useMemo(() => {
    if (!data?.sections) return [];
    return data.sections
      .map((s) => {
        const rows = s.rows.filter(rowMatches);
        const sortedRows = sortKeys.length === 0 ? rows : [...rows].sort((a, b) => compareBySortKeys(a, b, sortKeys, getSortValue));
        const subtotalRevenue = hasTableFilters
          ? rows.reduce((sum, r) => sum + (Number(r.revenue) || 0), 0)
          : s.subtotal?.revenue ?? rows.reduce((sum, r) => sum + (Number(r.revenue) || 0), 0);
        return { ...s, rows: sortedRows, subtotalRevenue };
      })
      .filter((s) => s.rows.length > 0);
  }, [data, rowMatches, sortKeys, getSortValue, hasTableFilters]);

  const grandTotal = useMemo(() => {
    if (hasTableFilters || data?.grandTotal?.revenue == null) {
      return visibleSections.reduce((sum, s) => sum + (Number(s.subtotalRevenue) || 0), 0);
    }
    return data.grandTotal.revenue;
  }, [visibleSections, hasTableFilters, data]);

  // Pagination — a global window over every section's rows in order (NOT a
  // per-section page count), so page size means "N rows per page" the same
  // way it does everywhere else in this app. A section split across a page
  // boundary keeps its own (always-correct, never-recomputed-per-page)
  // Subtotal row, but that row only renders on the chunk containing the
  // section's LAST row — showing the true section subtotal under only half
  // its rows, on an earlier page, would look like it belonged to what's on
  // screen when it doesn't.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [data, filterText, colFilters, sortKeys]);

  const totalRows = useMemo(() => visibleSections.reduce((sum, s) => sum + s.rows.length, 0), [visibleSections]);
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);

  const pagedSections = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    const end = start + pageSize;
    let cursor = 0;
    const result = [];
    for (const s of visibleSections) {
      const sectionStart = cursor;
      cursor += s.rows.length;
      const from = Math.max(0, start - sectionStart);
      const to = Math.min(s.rows.length, end - sectionStart);
      if (from < to) {
        result.push({ ...s, rows: s.rows.slice(from, to), showSubtotal: to === s.rows.length });
      }
    }
    return result;
  }, [visibleSections, safePage, pageSize]);

  const rangeStart = totalRows === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalRows);

  if (loading) return <Loading />;
  if (error) {
    return (
      <div style={{ padding: "40px 24px", textAlign: "center", color: "#D64550", fontSize: 13, fontFamily: FONT }}>
        {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div style={{ fontFamily: FONT, paddingBottom: 24 }}>
      <DetailsTable
        sections={pagedSections}
        grandTotal={grandTotal}
        nextYear={data.nextYear}
        sort={{ sortKeys, onThClick, getSortLabel }}
        filterState={{
          filterText, setFilterText, hasFilterValue,
          openFilterKey, toggleFilter, filterBtnRefs, filterPos,
          colFilters, setColFilters,
          openOpKey, setOpenOpKey, toggleOpDropdown, opBtnRefs, opPos,
        }}
        pagination={{
          page: safePage,
          pageSize,
          totalPages,
          totalRows,
          rangeStart,
          rangeEnd,
          onPageChange: setPage,
          onPageSizeChange: (n) => { setPageSize(n); setPage(1); },
        }}
      />
    </div>
  );
}
